/**
 * `SqlJsPersistence` — `Persistence` impl backed by sql.js (SQLite-in-WASM)
 * with the DB blob round-tripped through a pluggable `SqlJsBlobStore`
 * (IndexedDB in the browser; in-memory in tests). See ADR-0005 (stack),
 * ADR-0001 (interface insurance for the eventual server cutover).
 *
 * Write strategy
 *   Game-logic writes hit the in-memory SQLite synchronously through `db.run`.
 *   The expensive part is exporting the whole DB and persisting it to the
 *   blob store, so that step is debounced (~250 ms by default) and runs at
 *   most once per debounce window regardless of how many writes landed.
 *
 *   A `beforeunload` listener is attached in the browser to drive a final
 *   best-effort flush before the tab closes. `flush()` is also exposed for
 *   explicit end-of-session or test scenarios.
 *
 * sql.js imports stay inside `src/persistence/sqljs/` — game code talks to
 * the `Persistence` interface only.
 */

import type { Database } from 'sql.js';
import { makePlayerStarterInventory } from '../../combat/player';
import {
  type SerialisedInventory,
  deserialiseInventory,
  serialiseInventory,
} from '../../combat/serialise';
import type { Character, CharacterId } from '../../core/character';
import { type Hex, hexKey } from '../../core/hex';
import type {
  Tile,
  TileLifecycleState,
  TileMetadata,
  TileMutations,
  TileType,
} from '../../core/tile';
import { lifecycleKindOf } from '../../core/tile';
import { CHUNK_SIZE, type Chunk, type ChunkCoord } from '../../core/world';
import type { Persistence } from '../Persistence';
import { type LoadSqlJsOptions, type SqlJsBlobStore, bootDatabase } from './boot';

const DEFAULT_DEBOUNCE_MS = 250;

/**
 * Dormant realm-partition value (ADR-0018 §1 / HEX-98). Realms are retired —
 * there is one endless world. The `realm` / `death_realm` columns survive in the
 * schema (no DDL, no migration) but are now a FIXED partition the storage layer
 * writes; no domain type carries realm and no read path surfaces it. Every row
 * keys off this constant so the existing composite PKs `(realm, …)` stay intact.
 */
const WORLD_REALM = 1;

/** Shared character column list — keeps the three read paths (active roster, full
 * graveyard, single load) projecting the same shape `rowToCharacter` decodes. The
 * dormant `realm` / `death_realm` columns are deliberately not projected (HEX-98). */
const CHARACTER_SELECT =
  'SELECT id, name, position_q, position_r, explored, revealed, seen, hp, discoveries, gold, inventory, xp, level, created_at, died_at, death_q, death_r, death_cause FROM characters';

export interface SqlJsPersistenceOptions extends LoadSqlJsOptions {
  blobStore: SqlJsBlobStore;
  /** Debounce window for export → blob store. Defaults to 250 ms. */
  debounceMs?: number;
  /**
   * Whether to attach a `beforeunload` listener that drives a best-effort
   * flush. Defaults to true when a browser `window` is present, false otherwise.
   */
  attachBeforeUnload?: boolean;
}

export class SqlJsPersistence implements Persistence {
  private db: Database | null = null;
  private initPromise: Promise<void> | null = null;
  private dirty = false;
  private debounceHandle: ReturnType<typeof setTimeout> | null = null;
  private beforeUnloadHandler: (() => void) | null = null;
  private readonly debounceMs: number;

  constructor(private readonly opts: SqlJsPersistenceOptions) {
    this.debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  }

  init(): Promise<void> {
    if (!this.initPromise) {
      this.initPromise = this.bootstrap();
    }
    return this.initPromise;
  }

  private async bootstrap(): Promise<void> {
    this.db = await bootDatabase(this.opts);
    const shouldAttach =
      this.opts.attachBeforeUnload ??
      (typeof window !== 'undefined' && typeof window.addEventListener === 'function');
    if (shouldAttach && typeof window !== 'undefined') {
      this.beforeUnloadHandler = () => {
        void this.flush();
      };
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  // ─── characters ────────────────────────────────────────────────────────

  async listCharacters(): Promise<readonly Character[]> {
    const db = this.requireDb();
    // Active roster only — fallen characters (died_at IS NOT NULL) are archived
    // out of selection (HEX-65). The graveyard reads them via listAllCharacters.
    const result = db.exec(`${CHARACTER_SELECT} WHERE died_at IS NULL`);
    return result[0]?.values.map(rowToCharacter) ?? [];
  }

  async listAllCharacters(): Promise<readonly Character[]> {
    const db = this.requireDb();
    // Everyone ever — alive + fallen (HEX-65). Drives the graveyard/History view
    // and the hover name-cache so fallen `fallenCharacters` ids resolve to names.
    const result = db.exec(CHARACTER_SELECT);
    return result[0]?.values.map(rowToCharacter) ?? [];
  }

  async saveCharacter(character: Character): Promise<void> {
    const db = this.requireDb();
    const death = character.death ?? null;
    db.run(
      `INSERT INTO characters (id, name, realm, position_q, position_r, explored, revealed, seen, hp, discoveries, gold, inventory, dice, xp, level, created_at, died_at, death_realm, death_q, death_r, death_cause)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(id) DO UPDATE SET
         name = excluded.name,
         realm = excluded.realm,
         position_q = excluded.position_q,
         position_r = excluded.position_r,
         explored = excluded.explored,
         revealed = excluded.revealed,
         seen = excluded.seen,
         hp = excluded.hp,
         discoveries = excluded.discoveries,
         gold = excluded.gold,
         inventory = excluded.inventory,
         dice = excluded.dice,
         xp = excluded.xp,
         level = excluded.level,
         created_at = excluded.created_at,
         died_at = excluded.died_at,
         death_realm = excluded.death_realm,
         death_q = excluded.death_q,
         death_r = excluded.death_r,
         death_cause = excluded.death_cause`,
      [
        character.id,
        character.name,
        WORLD_REALM, // dormant partition (HEX-98) — column kept, no DDL.
        character.position.q,
        character.position.r,
        JSON.stringify([...character.explored]),
        JSON.stringify([...character.revealed]),
        JSON.stringify([...character.seen]),
        character.hp,
        character.discoveries,
        character.gold,
        JSON.stringify(serialiseInventory(character.inventory)),
        // Legacy owned-dice column (HEX-116), retired by ADR-0024: dice now live as
        // `die`-cards in the loadout. Always NULL — the column is kept (no DDL) so
        // old rows still load; any legacy `dice` JSON is dropped on read.
        null,
        // XP + level (HEX-117). NULL only for an in-memory character built without
        // xp/level; reads back as xp=0, level=1 (the pre-HEX-117 default).
        character.xp ?? null,
        character.level ?? null,
        character.createdAt,
        death === null ? null : death.at,
        death === null ? null : WORLD_REALM, // dormant death_realm partition (HEX-98).
        death === null ? null : death.hex.q,
        death === null ? null : death.hex.r,
        death === null ? null : death.cause,
      ],
    );
    this.markDirty();
  }

  async loadCharacter(id: CharacterId): Promise<Character | null> {
    const db = this.requireDb();
    // Loads any row, alive or fallen. The active-only gate is at listCharacters /
    // CharacterFlow.selectCharacter (HEX-65) — readers wanting a graveyard row
    // (or to resolve a fallen name) still get it here.
    const result = db.exec(`${CHARACTER_SELECT} WHERE id = ?`, [id]);
    const row = result[0]?.values[0];
    return row ? rowToCharacter(row) : null;
  }

  async deleteCharacter(id: CharacterId): Promise<void> {
    const db = this.requireDb();
    db.run('DELETE FROM characters WHERE id = ?', [id]);
    this.markDirty();
  }

  // ─── chunks ────────────────────────────────────────────────────────────

  async saveChunk(coord: ChunkCoord, chunk: Chunk): Promise<void> {
    const db = this.requireDb();
    const tilesBlob = new TextEncoder().encode(JSON.stringify(chunk.tiles));
    db.run(
      `INSERT INTO chunks (realm, cq, cr, seed, tiles)
       VALUES (?, ?, ?, ?, ?)
       ON CONFLICT(realm, cq, cr) DO NOTHING`,
      [WORLD_REALM, coord.cq, coord.cr, chunk.seed, tilesBlob],
    );
    this.markDirty();
  }

  async loadChunk(coord: ChunkCoord): Promise<Chunk | null> {
    const db = this.requireDb();
    const result = db.exec(
      'SELECT cq, cr, seed, tiles FROM chunks WHERE realm = ? AND cq = ? AND cr = ?',
      [WORLD_REALM, coord.cq, coord.cr],
    );
    const row = result[0]?.values[0];
    if (!row) return null;
    const tilesBytes = row[3] as Uint8Array;
    const tiles = JSON.parse(new TextDecoder().decode(tilesBytes)) as Tile[];
    return {
      coord: { cq: row[0] as number, cr: row[1] as number },
      seed: row[2] as number,
      tiles,
    };
  }

  // ─── tile mutations ────────────────────────────────────────────────────

  async saveTileMutations(tile: Tile): Promise<void> {
    const db = this.requireDb();
    const meta = tile.metadata;
    const lifecycleJson = tile.lifecycleState === null ? null : JSON.stringify(tile.lifecycleState);
    db.run(
      `INSERT INTO tile_metadata
         (realm, q, r, discovered_by, discovered_at, uncovered_by, uncovered_at,
          last_traveller, last_travelled_at, fallen_count, fallen_characters,
          crossings, lifecycle_state, road_network_id)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
       ON CONFLICT(realm, q, r) DO UPDATE SET
         discovered_by = COALESCE(tile_metadata.discovered_by, excluded.discovered_by),
         discovered_at = COALESCE(tile_metadata.discovered_at, excluded.discovered_at),
         uncovered_by = COALESCE(tile_metadata.uncovered_by, excluded.uncovered_by),
         uncovered_at = COALESCE(tile_metadata.uncovered_at, excluded.uncovered_at),
         last_traveller = COALESCE(excluded.last_traveller, tile_metadata.last_traveller),
         last_travelled_at = COALESCE(excluded.last_travelled_at, tile_metadata.last_travelled_at),
         fallen_count = MAX(tile_metadata.fallen_count, excluded.fallen_count),
         fallen_characters = excluded.fallen_characters,
         crossings = MAX(tile_metadata.crossings, excluded.crossings),
         lifecycle_state = excluded.lifecycle_state,
         road_network_id = excluded.road_network_id`,
      [
        WORLD_REALM,
        tile.q,
        tile.r,
        meta.discoveredBy,
        meta.discoveredAt,
        meta.uncoveredBy,
        meta.uncoveredAt,
        meta.lastTraveller,
        meta.lastTravelledAt,
        meta.fallenCount,
        JSON.stringify([...meta.fallenCharacters]),
        tile.crossings,
        lifecycleJson,
        tile.roadNetworkId,
      ],
    );
    this.markDirty();
  }

  async loadTileMutationsForChunk(coord: ChunkCoord): Promise<ReadonlyMap<string, TileMutations>> {
    const db = this.requireDb();
    const qMin = coord.cq * CHUNK_SIZE;
    const qMax = qMin + CHUNK_SIZE - 1;
    const rMin = coord.cr * CHUNK_SIZE;
    const rMax = rMin + CHUNK_SIZE - 1;
    const result = db.exec(
      `SELECT q, r, discovered_by, discovered_at, uncovered_by, uncovered_at,
              last_traveller, last_travelled_at, fallen_count, fallen_characters,
              crossings, lifecycle_state, road_network_id
       FROM tile_metadata
       WHERE realm = ? AND q BETWEEN ? AND ? AND r BETWEEN ? AND ?`,
      [WORLD_REALM, qMin, qMax, rMin, rMax],
    );
    const out = new Map<string, TileMutations>();
    for (const row of result[0]?.values ?? []) {
      const q = row[0] as number;
      const r = row[1] as number;
      out.set(hexKey({ q, r }), rowToMutations(row));
    }
    return out;
  }

  /**
   * Read back the mutations row for a single tile. Not on the `Persistence`
   * interface — exposed for tests and future readers.
   */
  async loadTileMutations(hex: Hex): Promise<TileMutations | null> {
    const db = this.requireDb();
    const result = db.exec(
      `SELECT q, r, discovered_by, discovered_at, uncovered_by, uncovered_at,
              last_traveller, last_travelled_at, fallen_count, fallen_characters,
              crossings, lifecycle_state, road_network_id
       FROM tile_metadata WHERE realm = ? AND q = ? AND r = ?`,
      [WORLD_REALM, hex.q, hex.r],
    );
    const row = result[0]?.values[0];
    return row ? rowToMutations(row) : null;
  }

  // ─── world clock ─────────────────────────────────────────────────────────

  /**
   * Read the world clock (HEX-99 / ADR-0018 §5) — the single world-scoped
   * movement counter held in the one-row `world_clock` table. An absent row
   * (fresh world, or a pre-v10 save the migration only created the empty table
   * for) reads as 0, so a legacy world resumes its clock from zero.
   */
  async loadWorldTick(): Promise<number> {
    const db = this.requireDb();
    const result = db.exec('SELECT tick FROM world_clock WHERE id = 0');
    const value = result[0]?.values[0]?.[0];
    return value == null ? 0 : Number(value);
  }

  /**
   * Persist the world clock. Upserts the single sentinel row (`id = 0`); the
   * count is world-scoped, not keyed by realm/character (realms retired, HEX-98).
   */
  async saveWorldTick(tick: number): Promise<void> {
    const db = this.requireDb();
    db.run(
      `INSERT INTO world_clock (id, tick) VALUES (0, ?)
       ON CONFLICT(id) DO UPDATE SET tick = excluded.tick`,
      [tick],
    );
    this.markDirty();
  }

  // ─── lifecycle ─────────────────────────────────────────────────────────

  /** Force a synchronous flush of pending writes to the blob store. */
  async flush(): Promise<void> {
    if (this.debounceHandle !== null) {
      clearTimeout(this.debounceHandle);
      this.debounceHandle = null;
    }
    if (!this.dirty) return;
    this.dirty = false;
    const db = this.requireDb();
    const blob = db.export();
    await this.opts.blobStore.write(blob);
  }

  async close(): Promise<void> {
    if (this.beforeUnloadHandler && typeof window !== 'undefined') {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    await this.flush();
    this.db?.close();
    this.db = null;
  }

  // ─── internals ─────────────────────────────────────────────────────────

  private requireDb(): Database {
    if (!this.db) {
      throw new Error('SqlJsPersistence.init() must complete before any read/write call');
    }
    return this.db;
  }

  private markDirty(): void {
    this.dirty = true;
    if (this.debounceHandle !== null) return;
    this.debounceHandle = setTimeout(() => {
      this.debounceHandle = null;
      void this.flush();
    }, this.debounceMs);
  }
}

type CharacterRow = readonly [
  id: unknown,
  name: unknown,
  positionQ: unknown,
  positionR: unknown,
  explored: unknown,
  revealed: unknown,
  seen: unknown,
  hp: unknown,
  discoveries: unknown,
  gold: unknown,
  inventory: unknown,
  xp: unknown,
  level: unknown,
  createdAt: unknown,
  diedAt: unknown,
  deathQ: unknown,
  deathR: unknown,
  deathCause: unknown,
];

function rowToCharacter(row: readonly unknown[]): Character {
  const [
    id,
    name,
    positionQ,
    positionR,
    explored,
    revealed,
    seen,
    hp,
    discoveries,
    gold,
    inventory,
    xp,
    level,
    createdAt,
    diedAt,
    deathQ,
    deathR,
    deathCause,
  ] = row as CharacterRow;
  return {
    id: id as CharacterId,
    name: name as string,
    position: { q: positionQ as number, r: positionR as number },
    explored: new Set(JSON.parse(explored as string) as string[]),
    // NULL revealed = a pre-HEX-73 character → empty set (the v8→v9 migration
    // default, applied at read so the schema step stays a pure column add).
    revealed: new Set(revealed == null ? [] : (JSON.parse(revealed as string) as string[])),
    // NULL seen = a pre-HEX-128 character → empty set (the v13→v14 migration
    // default, applied at read so the schema step stays a pure column add).
    seen: new Set(seen == null ? [] : (JSON.parse(seen as string) as string[])),
    hp: Number(hp),
    discoveries: Number(discoveries ?? 0),
    gold: Number(gold ?? 0),
    // NULL inventory = a pre-HEX-63 character; map to the §5/§19 starter (the
    // v5→v6 migration default, applied at read so the migration step stays a
    // pure schema change). Re-persisted in its concrete form on the next save.
    inventory:
      inventory == null
        ? makePlayerStarterInventory()
        : deserialiseInventory(JSON.parse(inventory as string) as SerialisedInventory),
    // The legacy `dice` column (HEX-116) is retired by ADR-0024 and no longer
    // read: dice now live as `die`-cards inside `inventory`. The 3-die baseline is
    // implicit. Any legacy owned-dice JSON is dropped (run-scoped — no re-home).
    // NULL xp/level = a pre-HEX-117 character; map to xp=0, level=1 (the v11→v12
    // migration default, applied at read). Re-persisted in concrete form on next save.
    xp: xp == null ? 0 : Number(xp),
    level: level == null ? 1 : Number(level),
    createdAt: createdAt as number,
    // died_at is the alive/dead discriminator (HEX-65): NULL → alive (death
    // null); non-NULL → rebuild the frozen death record from the death_* columns.
    death:
      diedAt == null
        ? null
        : {
            at: Number(diedAt),
            hex: { q: Number(deathQ), r: Number(deathR) },
            cause: (deathCause as string | null) ?? null,
            hp: Number(hp),
          },
  };
}

function rowToMutations(row: readonly unknown[]): TileMutations {
  const metadata: TileMetadata = {
    discoveredBy: (row[2] as CharacterId | null) ?? null,
    discoveredAt: (row[3] as number | null) ?? null,
    uncoveredBy: (row[4] as CharacterId | null) ?? null,
    uncoveredAt: (row[5] as number | null) ?? null,
    lastTraveller: (row[6] as CharacterId | null) ?? null,
    lastTravelledAt: (row[7] as number | null) ?? null,
    fallenCount: Number(row[8] ?? 0),
    fallenCharacters: JSON.parse((row[9] as string | null) ?? '[]') as CharacterId[],
  };
  const lifecycleJson = (row[11] as string | null) ?? null;
  const lifecycleState = lifecycleJson === null ? null : parseLifecycleState(lifecycleJson);
  return {
    metadata,
    crossings: Number(row[10] ?? 0),
    lifecycleState,
    roadNetworkId: (row[12] as number | null) ?? null,
  };
}

function parseLifecycleState(json: string): TileLifecycleState | null {
  const parsed = JSON.parse(json) as TileLifecycleState | null;
  return parsed;
}

/**
 * Narrow a freshly-loaded `TileLifecycleState` against the tile's type so
 * callers can switch on `tile.type` and get the right shape. Throws on
 * mismatch — that's a bug in either the writer or a corrupt row.
 */
export function narrowLifecycleState(
  type: TileType,
  state: TileLifecycleState | null,
): TileLifecycleState | null {
  if (state === null) return null;
  const expected = lifecycleKindOf(type);
  if (expected === null) {
    throw new Error(`tile type ${type} expects null lifecycleState, got ${state.kind}`);
  }
  if (state.kind !== expected) {
    throw new Error(`tile type ${type} expects lifecycleState.kind=${expected}, got ${state.kind}`);
  }
  return state;
}
